import React, { Component } from "react";
import PropTypes from "prop-types";
import Overlay from "../Overlay";
import OverlayButton from "../OverlayButton";
import {
  EmailValidationError,
  PasswordValidationError,
  LoginFailedError,
  TooManyAttemptsError,
  LoginRecaptchaNotification,
  LoginRecaptchaError
} from "../LoginErrors";
import {
  validateEmail,
  serialize,
  isHandheld,
  oAuthHelper
} from "../App/utils";
import { setLocalStorage } from "../App/cache";
import "./css/login.css";
import { withLDConsumer } from "launchdarkly-react-client-sdk";

class Login extends Component {
  constructor() {
    super();
    this.loginForm = React.createRef();
    const refreshToken = JSON.parse(localStorage.getItem("refreshTokens"));
    this.state = {
      errors: {
        emailFailedValidation: false,
        passwordFailedValidation: false,
        loginFailed: false,
        genericFail: false,
        resetFailed: false,
        attemptsFailed: false
      },
      rememberMe: !!(refreshToken && refreshToken["customer-oauth"]),
      showLegalText: false,
      submitButtonDisabled: false,
      password: ""
    };

    this.validateFieldsAndSubmit = this.validateFieldsAndSubmit.bind(this);
    this.rememberMe = this.rememberMe.bind(this);
    this.showLegalText = this.showLegalText.bind(this);
    this.oauthWithProxyServiceAndGoogleReCaptcha = this.oauthWithProxyServiceAndGoogleReCaptcha.bind(
      this
    );
  }

  componentWillMount() {
    this.setState({ rememberMe: this.state.rememberMe || isHandheld() });
  }

  setOAuthTokenForNolo(response) {
    const tokenObj = JSON.parse(response);
    window.localStorage.setItem(
      "refreshTokens",
      JSON.stringify({ "customer-oauth": tokenObj.refresh_token })
    );
    window.sessionStorage.setItem(
      "accessTokens",
      JSON.stringify({ "customer-oauth": tokenObj.access_token })
    );
    this.props.setTokenObj(tokenObj);
  }

  setCustomerObject(response) {
    const customerObj = JSON.parse(response);
    customerObj.Addresses = customerObj.Addresses.map(address => {
      address.Type = address.AddressType || "";
      return address;
    });
    window.sessionStorage.setItem("Customer", JSON.stringify(customerObj));
    setLocalStorage({
      key: "CID",
      data: customerObj.CustomerID,
      freshness: 31536000000
    });
    this.props.setCustomerObj(customerObj);
  }

  getOauth(
    username,
    password,
    path,
    reCaptchaV3Token,
    timeout,
    reCaptchaV2Token
  ) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      const data = {
        username,
        password,
        grant_type: "password",
        validator_id: "VoldemortCredValidator",
        client_id: this.state.rememberMe ? "nolo-rm" : "nolo",
        scope: oAuthHelper.loggedInScope
      };
      request.onreadystatechange = () => {
        if (request.readyState === 4) {
          switch (request.status) {
            case 200:
              //OK
              resolve({ text: request.responseText });
              break;
            case 400:
              // OAuth Error
              reject({
                loginFailed: true,
                genericFail: false,
                emailFailedValidation: false,
                passwordFailedValidation: false,
                loginRecaptchaNotification: false,
                loginRecaptchaError: false
              });
              break;
            case 403:
              // Too many invalid login attempts
              reject({
                loginFailed: false,
                genericFail: false,
                emailFailedValidation: false,
                passwordFailedValidation: false,
                attemptsFailed: true,
                loginRecaptchaNotification: false,
                loginRecaptchaError: false
              });
              break;
            case 401:
              //OAuth Error
              reject({
                loginFailed: false,
                genericFail: false,
                emailFailedValidation: false,
                passwordFailedValidation: false,
                loginRecaptchaNotification: true,
                loginRecaptchaError: false
              });
              break;
            default:
              reject({
                loginFailed: false,
                genericFail: true,
                emailFailedValidation: false,
                passwordFailedValidation: false,
                loginRecaptchaNotification: false,
                loginRecaptchaError: false
              });
          }
        }
      };
      if (timeout) request.timeout = timeout;
      request.open("POST", path);
      request.setRequestHeader(
        "Content-Type",
        "application/x-www-form-urlencoded; charset=utf-8"
      );
      request.setRequestHeader("DPZ-Pattern", "Full");
      if (this.state.rememberMe) {
        request.setRequestHeader("Authorization", "Basic bm9sby1ybTo=");
      } else {
        request.setRequestHeader("Authorization", "Basic bm9sbw==");
      }
      if (reCaptchaV3Token) {
        request.setRequestHeader(
          "X-DPZ-CAPTCHA",
          `google-recaptcha-v3-enterprise-gnolo;token=${reCaptchaV3Token};action=authproxyservice/login`
        );
      } else if (reCaptchaV2Token) {
        request.setRequestHeader(
          "X-DPZ-CAPTCHA",
          `${reCaptchaV2Token}; google-recaptcha-v2-checkbox`
        );
      }
      request.send(serialize(data));
    });
  }

  customerLogin(response) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      const responseObj = JSON.parse(response);
      const data = {
        // loyaltyIsActive: true,
        rememberMe: this.state.rememberMe
      };

      request.onreadystatechange = () => {
        if (request.readyState === 4) {
          if (request.status === 200) {
            resolve(request.responseText);
          } else {
            reject({
              loginFailed: true,
              emailFailedValidation: false,
              passwordFailedValidation: false
            });
          }
        }
      };
      request.open("POST", `${window.ENVCONFIG.legacy_api}/power/login`);
      request.setRequestHeader(
        "Content-Type",
        "application/x-www-form-urlencoded; charset=utf-8"
      );
      request.setRequestHeader(
        "Authorization",
        `${responseObj.token_type} ${responseObj.access_token}`
      );
      request.send(serialize(data));
    });
  }

  rememberMe(event) {
    this.setState({ rememberMe: event.target.checked });
  }

  oauthWithProxyServiceAndGoogleReCaptcha(
    email,
    password,
    reCaptchaV3Token,
    reCaptchaV2Token
  ) {
    return this.getOauth(
      email,
      password,
      `${window.ENVCONFIG.authProxyService}/auth-proxy-service/token.oauth2`,
      reCaptchaV3Token || null,
      1000,
      reCaptchaV2Token || null
    ).then(response => {
      this.setOAuthTokenForNolo(response.text);
      return this.customerLogin(response.text);
    });
  }

  validateFieldsAndSubmit(event) {
    const gRecaptchaResponse = event.currentTarget.querySelector(
      "#g-recaptcha-response"
    );
    const email = event.target.username.value;
    const password = this.state.password;
    const emailFailedValidation = !validateEmail(email);
    const passwordFailedValidation = password.length === 0;
    const successfulLogin = response => {
      this.setCustomerObject(response);
      this.props.closeOverlay(event, true);
    };
    const handleError = errors => {
      this.setState({
        errors,
        submitButtonDisabled: errors.loginRecaptchaNotification || false
      });

      if (errors.loginRecaptchaNotification) {
        let loginRecaptchaErrorTimeout;
        if (!window.grecaptcha) {
          this.setState({
            errors: {
              loginRecaptchaError: true,
              loginRecaptchaNotification: false
            },
            submitButtonDisabled: false
          });
        } else {
          loginRecaptchaErrorTimeout = setTimeout(() => {
            const recaptchaContainer = document.getElementById(
              "js-recaptchaLogin"
            );
            if (
              recaptchaContainer &&
              recaptchaContainer.childElementCount === 0
            ) {
              this.setState({
                errors: {
                  loginRecaptchaError: true,
                  submitButtonDisabled: false
                },
                submitButtonDisabled: false
              });
            }
          }, 5000);

          const recaptchaLoginRecaptcha = window.grecaptcha.render(
            "js-recaptchaLogin",
            {
              sitekey: window.ENVCONFIG.googleRecaptchaV2Checkbox,
              callback: token => {
                clearInterval(loginRecaptchaErrorTimeout);
                this.loginForm.current.dispatchEvent(new Event("submit"));
                window.grecaptcha.reset(recaptchaLoginRecaptcha);
              },
              hl: "en"
            }
          );
        }
      }
    };
    event.preventDefault();
    this.setState({ submitButtonDisabled: true });

    if (emailFailedValidation || passwordFailedValidation) {
      this.setState({
        errors: {
          emailFailedValidation,
          passwordFailedValidation
        },
        submitButtonDisabled: false
      });
    } else {
      if (
        this.props.flags[
          window.ENVCONFIG.launchDarkly.flags.getGoogleReCaptchaToken
        ] &&
        !gRecaptchaResponse
      ) {
        this.props
          .getReCaptchaV3Token("authproxyservice/login")
          .then(reCaptchaV3Token => {
            this.oauthWithProxyServiceAndGoogleReCaptcha(
              email,
              password,
              reCaptchaV3Token
            )
              .then(successfulLogin)
              .catch(handleError);
          });
      } else {
        this.oauthWithProxyServiceAndGoogleReCaptcha(
          email,
          password,
          false,
          gRecaptchaResponse ? gRecaptchaResponse.value : false
        )
          .then(successfulLogin)
          .catch(handleError);
      }
    }
  }

  showLegalText() {
    if (this.state.showLegalText) {
      this.setState({ showLegalText: false });
    } else {
      this.setState({ showLegalText: true });
    }
  }

  render() {
    return (
      <Overlay closeOverlay={this.props.closeOverlay} className={"login"}>
        <h1>Sign In To Your Pizza Profile</h1>
        <h2 className="create-account">
          Don&apos;t have one?{" "}
          <button
            data-quid="create-account"
            type="button"
            onClick={this.props.createAccount}
          >
            Create One
          </button>
        </h2>
        <form
          ref={this.loginForm}
          data-quid="login-form"
          className="login-form"
          onSubmit={this.validateFieldsAndSubmit}
        >
          <div className="form-fields">
            {this.state.errors.loginFailed && <LoginFailedError />}
            {this.state.errors.attemptsFailed && (
              <TooManyAttemptsError showOverlay={this.props.showOverlay} />
            )}
            {this.state.errors.emailFailedValidation && (
              <EmailValidationError />
            )}
            <input
              data-quid="user-name"
              type="text"
              className="user-name"
              name="username"
              autoFocus
              placeholder="Email"
              disabled={!!this.props.customerEmail}
              defaultValue={this.props.customerEmail || ""}
            />
            {this.state.errors.passwordFailedValidation && (
              <PasswordValidationError />
            )}
            <input
              data-quid="password"
              type="password"
              className="password"
              name="password"
              placeholder="Password"
              value={this.state.password}
              onChange={e => {
                this.setState({ password: e.currentTarget.value });
              }}
            />
            <label className="keep-me-signed-in" htmlFor="keep_me_signed_in">
              <input
                data-quid="keep-me-signed-in"
                id="keep_me_signed_in"
                type="checkbox"
                name="keep_me_signed_in"
                checked={this.state.rememberMe}
                onChange={evt => this.rememberMe(evt)}
              />
              Keep me signed in
            </label>
            <button
              data-quid="forgot-password"
              type="button"
              className="forgot-password"
              onClick={() => this.props.showOverlay("forgotPassword")}
            >
              forgot password?
            </button>
            {this.state.errors.loginRecaptchaNotification && (
              <LoginRecaptchaNotification />
            )}
            <div id="js-recaptchaLogin"></div>
            {this.state.errors.loginRecaptchaError && <LoginRecaptchaError />}
          </div>
          <div className="button__container">
            <OverlayButton
              quid="sign-in-btn-no-cancel"
              buttonType={"button"}
              buttonText={"cancel"}
              buttonClass={"blue"}
              buttonAction={this.props.closeOverlay}
            />
            <OverlayButton
              quid="sign-in-btn-submit"
              buttonType={"submit"}
              buttonText={"submit"}
              buttonClass={"red"}
              disabled={this.state.submitButtonDisabled}
            />
          </div>
        </form>
        <button
          data-quid="legal-disclaimer"
          type="button"
          className="legal-disclaimer"
          onClick={this.showLegalText}
        >
          <p>
            Securely access your recent orders, saved locations, and payment
            methods.{" "}
          </p>
        </button>
        {this.state.showLegalText && (
          <div data-quid="legal-text" className="legal-text">
            <p>
              You will have the opportunity to select &quot;Keep me signed
              in&quot; checkbox when you create a Pizza Profile or sign in to
              your existing Pizza Profile for a quicker ordering experience. By
              checking this box, you allow Domino&apos;s to provide you with a
              more personalized experience in which you will be greeted by your
              first name and presented with (i) your Easy Order, (ii) a list of
              your recent orders and (iii) information about your local store.
              When you select &quot;Keep me signed in&quot;, you will remain
              signed in to your Pizza Profile on that particular computer or
              device for up to six months or until you select the &quot;sign
              out&quot; link or clear your computer&apos;s or device&apos;s
              cookies. Although you are signed in to your Pizza Profile account,
              you will be prompted for your password if you attempt to perform a
              sensitive action such as modifying the personal information in
              your Pizza Profile account or completing an order using a stored
              credit card. If you change your mind about remaining signed in,
              simply select the &quot;sign out&quot; link to deactivate this
              feature.
              <br />
              NOTE: To prevent others from accessing your Pizza Profile account,
              Domino&apos;s does not recommend the use of this feature on any
              public or shared computer or device.{" "}
            </p>
          </div>
        )}
      </Overlay>
    );
  }
}

Login.propTypes = {
  setTokenObj: PropTypes.func.isRequired,
  setCustomerObj: PropTypes.func.isRequired,
  createAccount: PropTypes.func.isRequired,
  closeOverlay: PropTypes.func.isRequired,
  showOverlay: PropTypes.func.isRequired,
  customerEmail: PropTypes.string
};

Login.defaultProps = {
  customerEmail: ""
};

export default withLDConsumer()(Login);
